/**
 * domainStore — active domain state (Zustand)
 *
 * activeDomain is persisted to sessionStorage under
 *   key: swan_active_domain_{schoolId}
 *
 * On load: restores from sessionStorage; if the stored domain is no longer in
 * the registry (archived), falls back to null (callers default to 'discipline').
 */

import { create } from 'zustand';

const SESSION_KEY_PREFIX = 'swan_active_domain_';

function getSessionKey(schoolId) {
  return `${SESSION_KEY_PREFIX}${schoolId}`;
}

function saveToSession(schoolId, domain) {
  if (!schoolId || typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(getSessionKey(schoolId), JSON.stringify(domain));
  } catch {
    // sessionStorage unavailable — fail silently
  }
}

function loadFromSession(schoolId) {
  if (!schoolId || typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(getSessionKey(schoolId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const useDomainStore = create((set, get) => ({
  /** Currently active domain — null until domains are loaded */
  activeDomain: null,

  /** All domains loaded from the registry */
  domains: [],

  /** True once the initial domain fetch / subscription fires */
  isLoaded: false,
  loadError: null,
  setLoadError: (loadError) => set({ loadError }),

  /** School whose registry produced domains; readers must wait across a school switch. */
  registrySchoolId: null,

  registryError: null,
  registryLoading: false,
  retryRegistry: () => {},

  /** Apply only this school's current subscription, preserving selection on errors. */
  setRegistryState: (registry) => {
    const previous = get();
    const sameSchool = previous.registrySchoolId === (registry.schoolId || null);
    let active = sameSchool ? previous.activeDomain : null;
    if (registry.loaded) {
      active ||= loadFromSession(registry.schoolId);
      active = registry.domains.find(domain => domain.id === active?.id && domain.active !== false) || null;
      saveToSession(registry.schoolId, active);
    }
    set({
      domains: registry.domains,
      activeDomain: active,
      registrySchoolId: registry.schoolId || null,
      isLoaded: registry.loaded,
      registryLoading: registry.loading,
      registryError: registry.error,
      loadError: registry.error,
      retryRegistry: registry.retry,
    });
  },

  /**
   * Set the active domain and persist to sessionStorage.
   * @param {Object|null} domain - DomainConfig or null
   * @param {string} [schoolId] - School ID for session key
   */
  setActiveDomain: (domain, schoolId) => {
    set({ activeDomain: domain });
    if (schoolId) saveToSession(schoolId, domain);
  },

  /**
   * Replace the full domain list.
   * Also validates activeDomain is still present; resets to null if archived/removed.
   * @param {Array} domains - Array of DomainConfig
   * @param {string} [schoolId] - School ID for session restore
   */
  setDomains: (domains, schoolId) => {
    const state = get();
    let active = state.registrySchoolId === (schoolId || null) ? state.activeDomain : null;

    // On first load, try to restore from sessionStorage
    if (!active && schoolId) {
      const stored = loadFromSession(schoolId);
      if (stored && domains.find(d => d.id === stored.id && d.active !== false)) {
        active = stored;
      }
    }

    // Validate that the current active domain is still in the list and active.
    // Always refresh to the latest doc so reactive changes (e.g. fields[]) propagate.
    if (active) {
      const stillValid = domains.find(d => d.id === active.id && d.active !== false);
      if (!stillValid) {
        active = null;
        if (schoolId) saveToSession(schoolId, null);
      } else {
        active = stillValid;
        if (schoolId) saveToSession(schoolId, stillValid);
      }
    }

    set({ domains, activeDomain: active, registrySchoolId: schoolId || null });
  },

  /**
   * Mark domains as loaded.
   * @param {boolean} loaded
   */
  setLoaded: (loaded) => set({ isLoaded: loaded }),

  /**
   * Restore active domain from sessionStorage (call on mount with schoolId).
   * @param {string} schoolId
   */
  restoreFromSession: (schoolId) => {
    const state = get();
    if (!schoolId || state.activeDomain) return;
    const stored = loadFromSession(schoolId);
    if (stored) {
      const stillValid = state.domains.find(d => d.id === stored.id && d.active !== false);
      if (stillValid) {
        set({ activeDomain: stored });
      }
    }
  },
}));

export { useDomainStore };
export default useDomainStore;
